import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import { formatCurrency } from "app/utils/formatCurrency";
import { nextStep } from "app/lib/store/slices/steps/loanTypesSteps";
import { useUpdateApplicationStatus } from "app/hooks/applicant/useUpdateApplicationStatus";
import Loader from "app/components/common/Loader";
import {
  getRentalRate,
  RateType,
  LoanFor,
  VehicleCondition,
} from "app/utils/rentalRate";
import {
  applicationFeeForPersonal,
  applicationFeeForUsed,
  assessmentPercentage,
  legalFee,
  verificationValuationFee,
} from "app/constants/loanCalculateFee";

import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import car_icon from "app/assets/images/car_icon.svg";
import { calculateLoan } from "app/utils/calculateLoan";
import { handleApiError } from "app/utils/handleApiError";
import { FaRegQuestionCircle } from "react-icons/fa";
import { Tooltip } from "react-tooltip";

const PreQualified: React.FC = () => {
  const { loanApplication, vehicleDetail, ...others } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );
  const JointapplicationData = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );
  const applicantsSavedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const isjointApplication = loanApplication?.applicationType === "joint";

  const {
    mutate: statueMutate,
    status: statusUpdateStatus,
    isPending: statusIsPending,
    isSuccess: statusIsSuccess,
    isError: statusIsError,
    error: statusError,
  } = useUpdateApplicationStatus();

  const { maxBorrowingAmount } = useSelector(
    (state: RootState) => state.loanTypesSteps?.serviceability
  );
  const {
    applicationType,
    loanTerm,
    loanAmount,
    balloonPayment,
    loanFor,
    rateType,
  } = loanApplication || {};

  const [loanAmountValue, setLoanAmountValue] = useState<number>(
    loanAmount ?? 0
  );

  const [monthlyPayment, setMonthlyPayment] = useState<number>(0);
  const [progressValues, setProgressValues] = useState<number>(0);
  const [getinterestRate, setgetinterestRate] = useState<number>(0);
  const [finalLoanAmount, setfinalLoanAmount] = useState<number>(0);
  const [hasEstablishmentFee, setEstablishmentFee] = useState<number>(0);
  const [assessmentMonthlyRepayment, setassessmentMonthlyRepayment] =
    useState<number>(0);

  // const [monthlyInterestRateValue, setmonthlyInterestRateValue] =
  //   useState<any>();

  const [slideMaxValue, setSlideMaxValue] = useState<number>(
    Math.min(Number(maxBorrowingAmount), 100000)
  );

  const handleSliderChange = (value: number | number[]) => {
    if (Array.isArray(value)) {
      setLoanAmountValue(value[0]);
    } else {
      setLoanAmountValue(value);
    }
  };

  const dispatch = useDispatch();
  const {
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = (data: FormData) => {
    statueMutate(
      {
        url: `/loan-application/${applicantsSavedIds?.loanApplicationID}/status`,

        data: {
          profitRate: Number(getinterestRate.toFixed(2)),
          minRepayment: monthlyPayment.toFixed(2),
          applicationFee:
            loanApplication?.loanFor === "personal"
              ? applicationFeeForPersonal
              : applicationFeeForUsed,
          establishmentFee: hasEstablishmentFee ?? 0,
          legalFee: legalFee,
          verificationValuationFee: verificationValuationFee,
          applicationStatus: "approved",
          ...(rateType === "variable" && {
            assessmentMonthlyRepayment: assessmentMonthlyRepayment,
          }),
        },
      },
      {
        onSuccess: (data) => {
          dispatch(nextStep());
        },
        onError: (error: any) => {
          handleApiError(error);
          console.error("Error updating status", error);
        },
      }
    );
  };

  useEffect(() => {
    let rentalRate: number | undefined;
    const loanTermYears = Number(loanTerm ?? 0);

    setfinalLoanAmount(loanAmount ?? 0);

    const currentYear = new Date().getFullYear();
    const isPropertyOwner = isjointApplication
      ? JointapplicationData?.YourDetails?.mailingAddress?.housingStatus ===
        "Owner"
      : others?.applicants?.mailingAddress?.housingStatus === "Owner";
    const vehicleCondition = vehicleDetail?.vehicleCondition;
    const modelYears = currentYear - (vehicleDetail?.year ?? 0);

    const isRateType = (value: unknown): value is RateType =>
      typeof value === "string" && ["fixed", "variable"].includes(value);
    const isLoanFor = (value: unknown): value is LoanFor =>
      typeof value === "string" && ["personal", "business"].includes(value);
    const isVehicleCondition = (value: unknown): value is VehicleCondition =>
      typeof value === "string" && ["new", "demo", "used"].includes(value);
    if (
      isRateType(rateType) &&
      isLoanFor(loanFor) &&
      typeof isPropertyOwner === "boolean" &&
      isVehicleCondition(vehicleCondition) &&
      modelYears >= 0
    ) {
      rentalRate = getRentalRate({
        rateType,
        loanFor,
        isPropertyOwner,
        vehicleCondition,
        modelYears:
          vehicleCondition === "new" || vehicleCondition === "demo"
            ? 0
            : modelYears,
      });
    }

    if (
      loanAmount &&
      finalLoanAmount &&
      vehicleDetail?.price &&
      balloonPayment !== undefined
    ) {
      if (rentalRate && loanFor) {
        setgetinterestRate(Number(rentalRate) || 0);
        const result = calculateLoan(
          vehicleDetail?.price, // purchasePrice
          Number(vehicleDetail?.price) - Number(loanAmount), // contribution
          rentalRate, // annualRentalRate
          loanTermYears, // loanTermInYears
          Number(balloonPayment ?? 0), // balloonPercentage
          loanFor // loan for
        );

        // get the value for assessmentMonthlyRepayment and assessmentRate if the rateType is variable

        if (rateType === "variable" && assessmentPercentage) {
          const variableRentalRate =
            Number(rentalRate) + Number(assessmentPercentage);

          const variableResult = calculateLoan(
            vehicleDetail?.price, // purchasePrice
            Number(vehicleDetail?.price) - Number(loanAmount), // contribution
            variableRentalRate, // annualRentalRate
            loanTermYears, // loanTermInYears
            Number(balloonPayment ?? 0), // balloonPercentage
            loanFor // loan for
          );
          if (variableResult.monthlyPayment) {
            const variableMonthlyPayment =
              Number(variableResult?.monthlyPayment) + 15;
            setassessmentMonthlyRepayment(variableMonthlyPayment);
          }
        }

        if (result.monthlyPayment) {
          setEstablishmentFee(Number(result?.establishmentFee) ?? 0);
          const monthlyPayment = Number(result?.monthlyPayment) + 15;
          setMonthlyPayment(Number(monthlyPayment));
        } else {
          setMonthlyPayment(0);
        }
      }
    }

    const TotayValues =
      Number(maxBorrowingAmount) > 100000 ? 100000 : Number(maxBorrowingAmount);
    setSlideMaxValue(TotayValues);
    const progressValue = (finalLoanAmount / TotayValues) * 100;
    setProgressValues(progressValue);
  }, [loanApplication, finalLoanAmount]);

  return (
    <>
      {statusIsPending && <Loader />}
      <div className="sm:mt-10">
        <div className="w-full  sm:w-[80%] xl:w-[60%] mx-auto text-center px-4 sm:px-0">
          <MainTitle
            title={`Congratulations! You qualify for a ${formatCurrency(
              loanAmountValue
            )} auto loan with Hejaz`}
          />

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="text-left">
              <div className="mt-[8rem] mb-5">
                <Slider
                  min={0}
                  max={slideMaxValue}
                  value={loanAmountValue}
                  onChange={handleSliderChange}
                  disabled={true}
                  trackStyle={{ backgroundColor: "#D1A960", height: 5 }}
                  railStyle={{ backgroundColor: "#EAECF0", height: 5 }}
                  handleStyle={{
                    height: 0,
                    width: 0,
                    border: "none",
                    boxShadow: "none",
                  }}
                  handleRender={(renderProps) => {
                    const { props } = renderProps;
                    return (
                      <div
                        {...props}
                        style={{
                          ...props.style,
                          height: "25px",
                          width: "fit-content",
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          top: "-30px",
                        }}
                      >
                        <img
                          src={car_icon}
                          alt="car_icon"
                          className="w-[105px] h-[31px] sm:w-[145px] sm:h-[44px] ml-20 sm:ml-0"
                        />
                        <div className="absolute top-[-50px] left-[20%] ml-10 sm:ml-0 text-black font-bold whitespace-nowrap z-[999] text-[20px] md:text-[32px]">
                          {loanAmountValue ? (
                            <>
                              {formatCurrency(loanAmountValue)}
                              <FaRegQuestionCircle
                                color="#D1A960"
                                className="text-[14px] cursor-pointer z-[99999] absolute  top-[30%] right-[-20px]"
                                data-tooltip-id={`tooltip-loanAmountValue`}
                              />
                              <Tooltip
                                id={`tooltip-loanAmountValue`}
                                place="top"
                                className="font-Mulish"
                                style={{
                                  backgroundColor: "white",
                                  color: "#374151",
                                  fontSize: "14px",
                                  padding: "8px 12px",
                                  borderRadius: "8px",
                                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                                  border: "1px solid #E5E7EB",
                                  maxWidth: "280px",
                                  fontWeight: 400,
                                  zIndex: 999,
                                }}
                              >
                                Loan amount is exclusive of fees
                              </Tooltip>
                            </>
                          ) : (
                            0
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
              </div>
              <div className="flex justify-between flex-wrap items-center mb-4">
                <div className="">
                  <h6 className="flex items-center gap-1 font-Mulish text-lightBlue text-base">
                    Profit Rate{" "}
                  </h6>
                </div>{" "}
                <div className="">
                  <h6 className="flex items-center gap-1 font-Mulish text-lightBlue text-base">
                    Min. Repayments{" "}
                    <FaRegQuestionCircle
                      color="#D1A960"
                      className="text-[14px] cursor-pointer "
                      data-tooltip-id={`tooltip-repayments`}
                    />
                    <Tooltip
                      id={`tooltip-repayments`}
                      place="top"
                      className="font-Mulish"
                      style={{
                        backgroundColor: "white",
                        color: "#374151",
                        fontSize: "14px",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                        border: "1px solid #E5E7EB",
                        maxWidth: "250px",
                        zIndex: 999,
                      }}
                    >
                      Monthly payment is inclusive of loan fee
                    </Tooltip>
                  </h6>
                </div>
              </div>
              <div className="flex justify-between flex-wrap items-center">
                <div className="text-[20px] sm:text-[24px] font-Jakarta  text-darkgrey font-bold">
                  {getinterestRate ? getinterestRate.toFixed(2) + "%" : "0%"}
                </div>
                <div className="text-[20px] sm:text-[24px] font-Jakarta text-darkgrey font-bold">
                  {monthlyPayment.toFixed(2)}/mth
                </div>
              </div>
              <div className="mt-6">
                <p className="text-[14px] font-Mulish text-[#585C7B]">
                  To progress your application towards obtaining formal approval
                  please proceed to verify your application
                </p>
              </div>
            </div>
            <div className="flex md:justify-end xl:justify-end my-10 mt-12">
              <ContinueButton
                className="!w-[98%] sm:!w-[221px] h-[48px] text-[14px] font-Acumin font-semibold"
                type="submit"
              >
                Verify your application
              </ContinueButton>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default PreQualified;
